import { Progress } from "@/components/ui/progress";
import { Pause, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface MiniAudioPlayerProps {
  trackName: string;
  artistName: string;
  imageUrl: string;
  progress: number; // 0-100
  duration: number; // in seconds
  currentTime: number; // in seconds
  source: 'spotify' | 'youtube';
  isPlaying?: boolean;
  onStop: () => void;
  onTogglePlayPause?: () => void;
}

export const MiniAudioPlayer = ({
  trackName,
  artistName,
  imageUrl,
  progress,
  duration,
  currentTime,
  source,
  isPlaying = true,
  onStop,
  onTogglePlayPause,
}: MiniAudioPlayerProps) => {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="w-full mb-3"
      >
        <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-full shadow-lg px-2 py-1.5 flex items-center gap-2">
          {/* Album Art */}
          <img 
            src={imageUrl} 
            alt={trackName}
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
          
          {/* Track Info & Progress */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate leading-tight">{trackName}</p>
                <p className="text-[10px] text-muted-foreground truncate leading-tight">{artistName}</p>
              </div>
              <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                {formatTime(currentTime)}/{formatTime(duration)}
              </span>
            </div>
            
            {/* Progress bar */}
            <Progress value={progress} className="h-1" />
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={onTogglePlayPause}
              className="h-7 w-7 rounded-full"
            >
              {isPlaying ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
            </Button>
            
            <Button
              size="icon"
              variant="ghost"
              onClick={onStop}
              className="h-7 w-7 rounded-full hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
